import L from 'leaflet'
import { Marker, useMap } from 'react-leaflet'
import { useEffect, useMemo, useRef } from 'react'

import {
  DriverTrackingMarker,
  hubIcon,
  LoadingMarker,
} from 'components/TrackingMap'

import { useLiveTrackingTruckStore } from 'store/liveTrackingTruck'
import useReceiveMessage from 'hooks/useReceiveMessage'
import { ActionPostMessageEnum } from 'constants/postMessage'
import useMqttSubcribtion from 'hooks/useMqttSubcribtion'

import type { TruckItemType, TruckSubcribtionsType, LatLngPositionType } from './type'

/**
 * @default Monas Lat Long — shown before any location is selected
 */
export const centroidPosition: LatLngPositionType = [-6.175392, 106.827153]

const Content = () => {
  const map = useMap()

  const {
    isLoading,
    userData,
    truckPosition,
    truckSelected,
    setIsLoading,
    setTruckPosition,
    setTruckSelected,
    setUpdateTruck,
  } = useLiveTrackingTruckStore()

  // fitBounds to all hub locations once on first userData set
  const didFitBoundsRef = useRef(false)
  useEffect(() => {
    if (!userData || userData.locations.length === 0 || didFitBoundsRef.current) return

    const positions: LatLngPositionType[] = userData.locations.map((loc) => [
      parseFloat(loc.latitude),
      parseFloat(loc.longitude),
    ])

    if (positions.length === 1) {
      map.flyTo(positions[0], 14, { duration: 0.8 })
    } else {
      const bounds = new L.LatLngBounds(positions)
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 14, animate: true })
    }

    didFitBoundsRef.current = true
  }, [userData, map])

  // all truck positions as a flat array for bounds fitting
  const allTruckPositions = useMemo(
    () => Object.values(truckPosition).map((t): LatLngPositionType => [t.lat, t.lon]),
    [truckPosition],
  )

  const recenterAllTrucks = () => {
    if (allTruckPositions.length) {
      const bounds = new L.LatLngBounds(allTruckPositions)
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
    } else if (userData) {
      // fly to centroid of all selected locations
      const latSum = userData.locations.reduce((s, l) => s + parseFloat(l.latitude), 0)
      const lonSum = userData.locations.reduce((s, l) => s + parseFloat(l.longitude), 0)
      const count = userData.locations.length
      map.flyTo([latSum / count, lonSum / count], 13, { duration: 0.5 })
    }
  }

  const recenterFocusTruck = (truckId: number) => {
    const pos = truckPosition[truckId]
    if (pos) {
      map.flyTo([pos.lat, pos.lon], 17, { duration: 0.5 })
    }
  }

  // MQTT: receive live position updates from broker
  useMqttSubcribtion<TruckSubcribtionsType>(({ meta, payload, userProperties }) => {
    const { truckId, truckName } = (userProperties as { truckId?: string; truckName?: string }) || {}

    if (truckId) {
      const data = {
        ...payload,
        id: +truckId,
        truck_name: truckName || '',
        has_order: meta.topicKey === 'order',
      }

      setTruckPosition(data, (truckHasAdded, positions) => {
        if (!truckHasAdded && !truckSelected) {
          const bounds = new L.LatLngBounds(positions)
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
        }
      })
      setIsLoading(false)
    }
  })

  // postMessage: UPDATE_TRUCK — sync list from admin polling
  useReceiveMessage<TruckItemType[]>(ActionPostMessageEnum.UPDATE_TRUCK, ({ payload }) => {
    setUpdateTruck(payload)
  })

  // postMessage: SELECT_TRUCK — select or deselect a truck
  useReceiveMessage<{ truckId: number | null }>(
    ActionPostMessageEnum.SELECT_TRUCK,
    ({ payload }) => {
      if (!payload.truckId) {
        setTruckSelected(null, () => recenterAllTrucks())
        return
      }

      const found = Object.values(truckPosition).find((t) => t.id === payload.truckId)
      if (found) {
        const truckItem = useLiveTrackingTruckStore.getState().listTruckActive.find(
          (t) => t.truck_id === payload.truckId,
        ) || null

        setTruckSelected(truckItem, () => recenterFocusTruck(payload.truckId as number))
      }
    },
  )

  // postMessage: SHOW_ALL_TRUCK — reset to full view
  useReceiveMessage(ActionPostMessageEnum.SHOW_ALL_TRUCK, () => {
    setTruckSelected(null, () => recenterAllTrucks())
  })

  // postMessage: RECENTER — zoom to selected truck or fit all
  useReceiveMessage<{ truckId: number | null }>(
    ActionPostMessageEnum.RECENTER,
    ({ payload }) => {
      if (payload.truckId) {
        recenterFocusTruck(payload.truckId)
      } else {
        recenterAllTrucks()
      }
    },
  )

  // click on truck marker — notify admin parent
  const onTruckClick = (truckId: number) => {
    const isCurrentlySelected = truckSelected?.truck_id === truckId

    window.parent.postMessage(
      {
        action: isCurrentlySelected
          ? ActionPostMessageEnum.SHOW_ALL_TRUCK
          : ActionPostMessageEnum.SELECT_TRUCK,
        payload: { truckId: isCurrentlySelected ? null : truckId },
      },
      process.env.REACT_APP_ADMIN_URL || '',
    )

    if (isCurrentlySelected) {
      setTruckSelected(null, () => recenterAllTrucks())
    } else {
      recenterFocusTruck(truckId)
    }
  }

  if (!userData) return null

  return (
    <>
      {/* hub marker per lokasi yang dipilih */}
      {userData.locations.map((loc) => (
        <LoadingMarker
          key={loc.id}
          isLoading={isLoading}
          position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}
        >
          <Marker
            icon={hubIcon}
            position={[parseFloat(loc.latitude), parseFloat(loc.longitude)]}
          />
        </LoadingMarker>
      ))}

      {/* truck markers */}
      {Object.values(truckPosition).map((truck) => (
        <DriverTrackingMarker
          key={truck.id}
          driverId={truck.id}
          driverPosition={[truck.lat, truck.lon]}
          driverName={truck.truck_name}
          driverStatus={truck.truck_status}
          latestUpdate={truck.timestamp}
          onClick={onTruckClick}
        />
      ))}
    </>
  )
}

export default Content
